import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {DialogContent, Grid, Typography, Box} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {GroupData} from '@/types/group';
import {useDispatch} from 'react-redux';
import {useAppSelector} from '@/redux-store/store';
import DialogForm from '@/components/ui/dialogs/form-dialog';
import {EventData} from '@/types/event';
import ConfirmationDialog from '@/components/ui/dialogs/confirmation-dialog';
import {deleteGroup} from '@/rest/groups/delete-group';
import {joinGroup} from '@/rest/groups/join-group';
import {leaveGroup} from '@/rest/groups/leave-group';
import AllEventsDialog from '@/components/events/all-events-dialog';
import DialogWithCloseIcon from '@/components/ui/dialogs/dialog-with-close-icon';
import {useSnackbar} from '@/contexts/snackbar';
import {getGroupById} from '@/rest/groups/get-group';
import GroupEditForm from '@/components/forms/groups/update';
import EventDetailsDialog from '@/components/events/details';
import EventCreateButton from './buttons/big-create-button';
import GroupInfo from './group-info';
import GroupDetailsButtons from './buttons/buttons';
import EventList from '@/components/events/event-list';
import {updateGroupInRedux} from '@/redux-store/slices/groups';
import ParticipantsList from '@/components/participant/participants-list';
import {useCompanyContext} from '@/contexts/admin/companies';
import {MatrixRoomProvider} from '@/contexts/matrix/room/provider';
import {useMatrixContext} from '@/contexts/matrix/client';
import {useMatrixRoomContext} from '@/contexts/matrix/room';
import KeycloakInstance from '@/services/keycloak';
import EventForm from '@/components/forms/events';
import {
  getCompanyLabel,
  isDocumentCompanyRoleHealthManager,
  isOneOfUserCompaniesDocument,
  isUserDocumentManager
} from '@/utils';
import {env} from '@/config';
import {getEventById} from '@/rest/events/get-event';
import {setCurrentGroup} from '@/redux-store/slices/auth';
import {useNavigate} from 'react-router-dom';

type GroupDetailsDialogProps = {
  open: boolean;
  onClose: () => void;
  group: GroupData;
  onChange?: (group: GroupData) => void;
  onDelete?: (groupId: string) => void;
  onClone?: () => void;
  groupOfEventChanged?: () => void;
  defaultChatTab?: boolean;
  eventId?: string;
  onEventClose?: () => void;
  onCloseGroupDetails?: () => void;
};

const GroupDetailsDialog: React.FC<GroupDetailsDialogProps> = ({
  // related to dialog
  open,
  onClose,
  group,
  onChange = () => {
    // noop
  },
  onDelete = () => {
    // noop
  },
  groupOfEventChanged = () => {
    // noop
  },
  defaultChatTab = false,
  eventId,
  onEventClose,
  onCloseGroupDetails
}) => {
  const {t} = useTranslation();
  const {APP_VARIANT} = env;

  const navigate = useNavigate();

  const {
    isAuthenticated,
    matrixCredentials,
    isUserHealthManager,
    keycloakUser,
    currentGroup
  } = useAppSelector((state) => state.auth);
  const userInfo = useAppSelector((state) => state.userInfos.userInfos);
  const trainerInSjOne =
    keycloakUser?.userRole === 'TRAINER' && APP_VARIANT === 'sjone';

  const companyLabel =
    group.company && getCompanyLabel(group.company, userInfo.companies || []);

  const {refreshCompanyData} = useCompanyContext() || {};

  const {showSnackbarMessage} = useSnackbar();

  const {matrixClient} = useMatrixContext();
  const {initMatrixRoom, userDialogOpen, multimediaDialogOpen} =
    useMatrixRoomContext();

  const [editGroupFormOpen, setEditGroupFormOpen] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

  const [eventDetailsOpen, setEventDetailsOpen] = useState(false);
  const [allEventsDialogOpen, setAllEventsDialogOpen] = useState(false);
  const [createEventFormOpen, setCreateEventFormOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventData | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<GroupData | null>(group);
  const [userOverviewDialogOpen, setUserOverviewDialogOpen] = useState(false);
  const [currentEventId, setCurrentEventId] = useState<string | null>(null);

  const dispatch = useDispatch();

  const isUserManager = isUserDocumentManager(selectedGroup, userInfo);

  const isUserMember = !!(
    selectedGroup &&
    selectedGroup.members &&
    selectedGroup.members.some((member) => member._id === userInfo?._id)
  );

  const trainerInSjOneNoMember = trainerInSjOne && !isUserMember;

  const trainerInSjOneNoManager = trainerInSjOne && !isUserManager;

  const isHealthManagerGroup =
    selectedGroup && isDocumentCompanyRoleHealthManager(selectedGroup);

  const companyGroupAndUserHealthManager =
    isUserHealthManager && isHealthManagerGroup;

  const isUserAuthorizedToSeeRoom = useMemo(() => {
    return !!isUserMember;
    //  || isUserManager || isHealthManager);
  }, [isUserMember]);

  const isUserAuthorizedToEditRoom = useMemo(() => {
    return !!(isUserMember || companyGroupAndUserHealthManager);
  }, [companyGroupAndUserHealthManager, isUserMember]);

  const eventsCreatingAllowed =
    (isUserManager ||
      (isUserMember && !group.trainerGroup) ||
      companyGroupAndUserHealthManager) &&
    !trainerInSjOneNoManager;

  const handleUpdateGroupData = useCallback(async () => {
    try {
      const updatedGroupData = await getGroupById(group._id);
      if (updatedGroupData) {
        setSelectedGroup(updatedGroupData);
      }
    } catch (error) {
      console.error('Could not update group ', error);
    }
  }, [group]);

  const onEventChange = useCallback(
    async (event: EventData) => {
      if (event && selectedGroup) {
        const response = await getGroupById(selectedGroup._id);
        dispatch(updateGroupInRedux(response));

        onChange(response);
        handleUpdateGroupData();
      }
    },
    [dispatch, handleUpdateGroupData, onChange, selectedGroup]
  );

  const onEventDelete = useCallback(
    async (eventId: string) => {
      if (selectedGroup) {
        if (selectedEvent && selectedEvent._id === eventId) {
          setEventDetailsOpen(false);
          setSelectedEvent(null);
        }
        const response = await getGroupById(selectedGroup._id);

        if (response) {
          dispatch(updateGroupInRedux(response));
        }

        onChange(response);
        handleUpdateGroupData();
      }
    },
    [dispatch, onChange, selectedEvent, selectedGroup, handleUpdateGroupData]
  );

  const onEventClone = useCallback(async () => {
    if (selectedGroup) {
      const response = await getGroupById(selectedGroup._id);
      refreshCompanyData();
      dispatch(updateGroupInRedux(response));

      onChange(response);
      handleUpdateGroupData();
    }
  }, [
    dispatch,
    onChange,
    selectedGroup,
    refreshCompanyData,
    handleUpdateGroupData
  ]);

  const onEventCreated = useCallback(async () => {
    showSnackbarMessage(t('event_create_success'), 'success');
    refreshCompanyData();
    setCreateEventFormOpen(false);
    if (selectedGroup) {
      const response = await getGroupById(selectedGroup._id);

      onChange(response);
      handleUpdateGroupData();
    }
  }, [
    onChange,
    selectedGroup,
    showSnackbarMessage,
    t,
    refreshCompanyData,
    handleUpdateGroupData
  ]);

  const onEventCreationError = useCallback(() => {
    showSnackbarMessage(t('event_create_failure'), 'error');
  }, [showSnackbarMessage, t]);

  const handleGroupEditButtonClick = useCallback(() => {
    setEditGroupFormOpen(true);
  }, []);

  const handleDeleteClick = useCallback(() => {
    if (selectedGroup) {
      const groupId = selectedGroup._id;
      deleteGroup(groupId)
        .then((response) => {
          if (response) {
            onClose();
            showSnackbarMessage(t('group_deleting_success'), 'success');
            onDelete(selectedGroup._id);
            refreshCompanyData();
          } else {
            showSnackbarMessage(t('group_deleting_failure'), 'error');
          }
        })
        .catch((error) => {
          console.error('Error deleting event:', error);
          showSnackbarMessage(t('group_deleting_failure'), 'error');
        })
        .finally(() => {
          setDeleteConfirmationOpen(false);
        });
    }
  }, [
    selectedGroup,
    onClose,
    showSnackbarMessage,
    t,
    onDelete,
    refreshCompanyData
  ]);

  const handleDeleteConfirmation = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setDeleteConfirmationOpen(true);
    },
    []
  );

  const handleGroupJoinClick = useCallback(() => {
    if (selectedGroup) {
      if (!isAuthenticated) {
        KeycloakInstance.loginExternal().catch((error) => {
          console.error('Login failed: ', error);
        });
        return;
      }
      const groupId = selectedGroup._id;
      joinGroup(groupId, userInfo._id)
        .then((response) => {
          if (response) {
            showSnackbarMessage(t('group_joining_success'), 'success');

            onChange(response);
            handleUpdateGroupData();
          } else {
            showSnackbarMessage(t('group_joining_failure'), 'error');
          }
        })
        .catch((error) => {
          console.error('Error joining group:', error);
          showSnackbarMessage(t('group_joining_failure'), 'error');
        });
    }
  }, [
    selectedGroup,
    isAuthenticated,
    showSnackbarMessage,
    t,
    onChange,
    handleUpdateGroupData,
    userInfo
  ]);

  const handleGroupLeaveClick = useCallback(async () => {
    if (selectedGroup) {
      const groupId = selectedGroup._id;
      try {
        const response = await leaveGroup(groupId);
        if (response) {
          showSnackbarMessage(t('group_leaving_success'), 'success');

          onChange(response);
          handleUpdateGroupData();
        } else {
          showSnackbarMessage(t('group_leaving_failure'), 'error');
        }
      } catch (error) {
        console.error('Error leaving group:', error);
        showSnackbarMessage(t('group_leaving_failure'), 'error');
      }
    }
  }, [selectedGroup, showSnackbarMessage, t, onChange, handleUpdateGroupData]);

  const handleCreateButtonClicked = useCallback(() => {
    setCreateEventFormOpen(true);
  }, []);

  const handleEventCreationCancel = useCallback(() => {
    setCreateEventFormOpen(false);
  }, []);

  const onEventCardClick = useCallback((event: EventData) => {
    setSelectedEvent(event);
    setEventDetailsOpen(true);
  }, []);

  const onGroupEdited = useCallback(async () => {
    showSnackbarMessage(t('group_update_success'), 'success');
    if (selectedGroup) {
      const response = await getGroupById(selectedGroup._id);

      onChange(response);
      handleUpdateGroupData();
    }
    setEditGroupFormOpen(false);
  }, [onChange, selectedGroup, showSnackbarMessage, t, handleUpdateGroupData]);

  const onGroupEditError = useCallback(() => {
    showSnackbarMessage(t('group_update_failure'), 'error');
  }, [showSnackbarMessage, t]);

  const handleGroupEditCancel = useCallback(() => {
    setEditGroupFormOpen(false);
  }, []);

  const handleOpenAllEventsDialog = useCallback(() => {
    setAllEventsDialogOpen(true);
  }, []);

  const handleCloseAllEventsDialog = useCallback(() => {
    setAllEventsDialogOpen(false);
  }, []);

  const handleCloseEventDetails = useCallback(() => {
    setEventDetailsOpen(false);
    setSelectedEvent(null);
    setCurrentEventId(null);
    if (onEventClose) {
      onEventClose();
    }
  }, [onEventClose]);

  const handleCloseConfirmationDialog = useCallback(() => {
    setDeleteConfirmationOpen(false);
  }, []);

  const handleUserOverviewOpenChange = useCallback((isOpen: boolean) => {
    setUserOverviewDialogOpen(isOpen);
  }, []);

  useEffect(() => {
    async function fetchGroupDetails() {
      if (group) {
        const response = await getGroupById(group._id);
        if (response) {
          setSelectedGroup(response);
        }
      }
    }
    fetchGroupDetails();
  }, [group]);

  useEffect(() => {
    if (selectedGroup) {
      if (isUserAuthorizedToSeeRoom && matrixClient && matrixCredentials) {
        console.log('init matrix room in group details');
        initMatrixRoom();
      }
    }
  }, [
    selectedGroup,
    matrixClient,
    initMatrixRoom,
    isUserManager,
    isUserMember,
    companyGroupAndUserHealthManager,
    isUserAuthorizedToSeeRoom,
    isUserAuthorizedToEditRoom,
    matrixCredentials
  ]);

  useEffect(() => {
    if (eventId && selectedGroup) {
      setCurrentEventId(eventId);
    }
  }, [eventId, selectedEvent, selectedGroup]);
  useEffect(() => {
    const openEventUsingId = async () => {
      if (selectedGroup && currentEventId) {
        const eventData = await getEventById(currentEventId);
        if (eventData) {
          if (APP_VARIANT === 'sjone') {
            if (!isOneOfUserCompaniesDocument(eventData, userInfo)) {
              navigate('/403');
            }
          }
          onEventCardClick(eventData);
        }
      }
    };

    openEventUsingId();
  }, [
    APP_VARIANT,
    currentEventId,
    navigate,
    onEventCardClick,
    selectedGroup,
    userInfo
  ]);

  useEffect(() => {
    if (selectedGroup && currentGroup !== selectedGroup._id) {
      dispatch(setCurrentGroup(selectedGroup._id));
    }
  }, [currentGroup, dispatch, selectedEvent, selectedGroup]);

  const anyOtherDialogOpen =
    createEventFormOpen ||
    eventDetailsOpen ||
    editGroupFormOpen ||
    allEventsDialogOpen ||
    !!eventId;

  return (
    selectedGroup && (
      <>
        <DialogWithCloseIcon
          open={open && !anyOtherDialogOpen}
          hide={
            userDialogOpen || multimediaDialogOpen || userOverviewDialogOpen
          }
          onClose={onClose}
          fullWidth={true}
          maxWidth="lg"
        >
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <GroupInfo
                  selectedGroup={selectedGroup}
                  onUserOverviewOpenChange={handleUserOverviewOpenChange}
                  isUserAuthorizedToSeeRoom={isUserAuthorizedToSeeRoom}
                  defaultChatTab={defaultChatTab}
                  trainerInSjOne={trainerInSjOne}
                  isUserManager={isUserManager || false}
                  companyName={companyLabel}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Box>
                  <Box marginBottom={2}>
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{textAlign: 'center'}}
                    >
                      {t('events_screen')}:
                    </Typography>
                  </Box>
                  {selectedGroup.events.length === 0 ? (
                    eventsCreatingAllowed && (
                      <Grid item xs={12} md={12} marginBottom={{xs: 1, sm: 0}}>
                        <EventCreateButton
                          onClick={handleCreateButtonClicked}
                        />
                      </Grid>
                    )
                  ) : (
                    <>
                      <Grid container spacing={1} marginBottom={{xs: 1, sm: 0}}>
                        <EventList
                          events={selectedGroup.events}
                          onEventCardClick={onEventCardClick}
                          handleCreateButtonClicked={handleCreateButtonClicked}
                          eventsCreatingAllowed={eventsCreatingAllowed || false}
                          handleOpenAllEventsDialog={handleOpenAllEventsDialog}
                          smallerVariant={true}
                          trainerInSjOneNoManager={trainerInSjOneNoManager}
                        />
                      </Grid>

                      <Grid container justifyContent="flex-end">
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          lg={4}
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-end'
                          }}
                        ></Grid>
                      </Grid>
                    </>
                  )}
                </Box>
              </Grid>
            </Grid>

            <Grid container spacing={0}>
              {!trainerInSjOneNoMember && (
                <Box marginTop={1}>
                  <ParticipantsList
                    onUserOverviewOpenChange={handleUserOverviewOpenChange}
                    participants={selectedGroup.members}
                    groupView={true}
                  />
                </Box>
              )}

              <GroupDetailsButtons
                selectedGroup={selectedGroup}
                handleDeleteConfirmation={handleDeleteConfirmation}
                handleGroupEditButtonClick={handleGroupEditButtonClick}
                handleGroupJoinClick={handleGroupJoinClick}
                handleGroupLeaveClick={handleGroupLeaveClick}
                isTrainerInSjOne={trainerInSjOne}
              />
            </Grid>
          </DialogContent>
        </DialogWithCloseIcon>

        {/* dialogs used within component */}
        <DialogForm
          open={createEventFormOpen}
          defaultGroup={selectedGroup._id}
          onSubmit={onEventCreated}
          onCancel={handleEventCreationCancel}
          onError={onEventCreationError}
          FormComponent={EventForm}
          mode={'create'}
          maxHeightAllowed={'1200px'}
        />
        {selectedEvent && (
          <>
            <MatrixRoomProvider childId={selectedEvent._id}>
              <EventDetailsDialog
                open={eventDetailsOpen}
                onClose={handleCloseEventDetails}
                event={selectedEvent}
                onChange={onEventChange}
                onDelete={onEventDelete}
                onClone={onEventClone}
                myGroupAsDefaultInCloneForm={true}
                groupOfEventChanged={groupOfEventChanged}
                onCloseGroupDetails={onCloseGroupDetails}
              />
            </MatrixRoomProvider>
          </>
        )}
        <DialogForm
          open={editGroupFormOpen}
          onSubmit={onGroupEdited}
          onCancel={handleGroupEditCancel}
          onError={onGroupEditError}
          FormComponent={GroupEditForm}
          initialValues={selectedGroup}
          maxHeightAllowed="1200px"
        />

        <AllEventsDialog
          events={selectedGroup.events}
          onClose={handleCloseAllEventsDialog}
          allEventsDialogOpen={allEventsDialogOpen}
          onChange={onEventChange}
          onDelete={onEventDelete}
          onClone={onEventClone}
        />
        <ConfirmationDialog
          open={deleteConfirmationOpen}
          title={t('delete_group_dialog_title')}
          message={t('delete_group_dialog_content')}
          onConfirm={handleDeleteClick}
          onCancel={handleCloseConfirmationDialog}
          warn
        />
      </>
    )
  );
};

export default GroupDetailsDialog;
